import { ROUTE, UI } from '../config'
import { BRAND_SPECIFIC_TAG } from '../containers/BrandSpecificInformation/BrandSpecificInformation'
import { isBrowseRootRoute } from './buildBrowseGuideBreadcrumbs'
import { OWNERS_MANUAL_TITLE } from './utils'

/**
 * Filters browse menu items to those with offline content saved
 *
 * @param {Array} items browse list items
 * @param {Set} downloadedCategories downloaded category titles
 * @returns {Array}
 */
export function filterItemsByDownloadedCategories(items, downloadedCategories) {
  if (!items || !items.length) return []
  if (!downloadedCategories || !downloadedCategories.size) return items

  const filtered = items.filter(item => item.title && downloadedCategories.has(item.title))
  return filtered.length ? filtered : items
}

/**
 * Resolves the top-level browse category for the current route
 *
 * @param {object} match react-router match
 * @param {string} baseRoute guide base route
 * @returns {string|null} category title or null
 */
export function getBrowseParentCategory(match, baseRoute) {
  const browseBase = `${baseRoute}${ROUTE.BROWSE}`
  const { url, params } = match

  if (!url.startsWith(browseBase)) return null

  if (url.includes(`${browseBase}${ROUTE.PRINTABLE_OWNERS_MANUAL}`)) {
    return UI.PRINTABLE_OWNERS_MANUAL
  }

  if (url.includes(`${browseBase}${ROUTE.PDFS}`)) {
    return 'Component Manual'
  }

  if (url.includes(`${browseBase}${ROUTE.VIDEOS}`)) {
    return 'Videos'
  }

  if (url.includes(`${browseBase}${ROUTE.BRAND_SPECIFIC}`)) {
    return BRAND_SPECIFIC_TAG
  }

  if (url.includes(`${browseBase}${ROUTE.ARTICLES}`)) {
    // Article sub-folders live under Owner's Manual; offline downloads track the parent category.
    return UI.OWNERS_MANUAL
  }

  if (url.includes(`${browseBase}${ROUTE.TAGS}`)) {
    if (params.tag) {
      const tag = decodeURIComponent(params.tag)
      if (tag === OWNERS_MANUAL_TITLE || tag === UI.OWNERS_MANUAL) {
        return UI.OWNERS_MANUAL
      }
      return tag
    }
    return null
  }

  if (
    url.includes(`${browseBase}${ROUTE.TOPIC}`) ||
    url.includes(`${browseBase}${ROUTE.FOLDER}`) ||
    (params.folderId && !isBrowseRootRoute(match, baseRoute))
  ) {
    return UI.OWNERS_MANUAL
  }

  return null
}

/**
 * Returns whether two browse routes belong to the same offline parent category
 *
 * @param {object} prevMatch previous react-router match
 * @param {object} nextMatch current react-router match
 * @param {string} baseRoute guide base route
 * @returns {boolean}
 */
export function isSameBrowseOfflineSection(prevMatch, nextMatch, baseRoute) {
  const prevParent = getBrowseParentCategory(prevMatch, baseRoute)
  const nextParent = getBrowseParentCategory(nextMatch, baseRoute)

  return prevParent !== null && prevParent === nextParent
}

/**
 * Returns browse layout props passed to child render functions
 *
 * @param {boolean} offline whether the app is offline
 * @param {Set} downloadedCategories downloaded category titles
 * @param {boolean} offlineBrowseReady whether offline category state has loaded
 * @returns {object}
 */
export function getOfflineBrowseChildProps(offline, downloadedCategories, offlineBrowseReady) {
  return {
    offlineBrowseActive: offline,
    offlineBrowseReady,
    downloadedCategories,
    filterBrowseMenuItems: items => filterItemsByDownloadedCategories(
      items,
      offline && offlineBrowseReady ? downloadedCategories : null
    ),
  }
}
